import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from './AuthContext';

export interface Notification {
  id: string;
  user_id?: number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  link?: string;
  is_read: boolean;
  createdAt: string;
}

export type AppNotification = Notification;

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  addNotification: (notification: Omit<Notification, 'id' | 'is_read' | 'createdAt' | 'user_id'>) => void;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  fetchNotifications: () => Promise<void>;
  isDrawerOpen: boolean;
  setDrawerOpen: (isOpen: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();

  const inFlightUpdatesRef = useRef<Set<string>>(new Set());
  const isMarkingAllRef = useRef<boolean>(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/notifications');
      const rawData = response.data;
      const list = Array.isArray(rawData) ? rawData : (rawData?.data || []);
      setNotifications(list);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch and clear on logout
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    fetchNotifications();
  }, [user, fetchNotifications]);

  // Fetch when window gets focus
  useEffect(() => {
    if (!user) return;
    const handleFocus = () => {
      fetchNotifications();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, fetchNotifications]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'is_read' | 'createdAt' | 'user_id'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      user_id: user?.id,
      is_read: false,
      createdAt: new Date().toISOString(),
    };

    setNotifications((prev) => [newNotification, ...prev]);

    switch (notification.type) {
      case 'success':
        toast.success(notification.title);
        break;
      case 'warning':
        toast.warning(notification.title);
        break;
      case 'error':
        toast.error(notification.title);
        break;
      default:
        toast.info(notification.title);
        break;
    }
  }, [user]);

  // Listen for local trigger events
  useEffect(() => {
    const handleEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        addNotification(customEvent.detail);
      }
      fetchNotifications();
    };

    window.addEventListener('app:add-notification', handleEvent);
    window.addEventListener('lympho:add-notification', handleEvent);
    return () => {
      window.removeEventListener('app:add-notification', handleEvent);
      window.removeEventListener('lympho:add-notification', handleEvent);
    };
  }, [addNotification, fetchNotifications]);

  const markAsRead = async (id: string) => {
    const target = notifications.find(n => n.id === id);
    if (!target || target.is_read) return;
    if (inFlightUpdatesRef.current.has(id)) return;

    inFlightUpdatesRef.current.add(id);

    // Optimistic UI update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));

    try {
      await api.put(`/notifications/${id}/read`);
    } catch (err) {
      console.error('Failed to mark as read:', err);
      // Revert optimistic update
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: false } : n));
      toast.error('Failed to mark notification as read. Please try again.');
    } finally {
      inFlightUpdatesRef.current.delete(id);
    }
  };

  const markAsUnread = async (id: string) => {
    const target = notifications.find(n => n.id === id);
    if (!target || !target.is_read) return;
    if (inFlightUpdatesRef.current.has(id)) return;

    inFlightUpdatesRef.current.add(id);

    // Optimistic UI update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: false } : n));

    try {
      await api.put(`/notifications/${id}/unread`);
    } catch (err) {
      console.error('Failed to mark as unread:', err);
      // Revert optimistic update
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      toast.error('Failed to mark notification as unread. Please try again.');
    } finally {
      inFlightUpdatesRef.current.delete(id);
    }
  };

  const markAllAsRead = async () => {
    const hasUnread = notifications.some(n => !n.is_read);
    if (!hasUnread) return;
    if (isMarkingAllRef.current) return;

    isMarkingAllRef.current = true;
    const previousNotifications = notifications;

    // Optimistic UI update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));

    try {
      await api.put('/notifications/mark-all-read');
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      // Revert optimistic update
      setNotifications(previousNotifications);
      toast.error('Failed to mark all notifications as read. Please try again.');
    } finally {
      isMarkingAllRef.current = false;
    }
  };

  const removeNotification = async (id: string) => {
    const previousNotifications = notifications;

    // Optimistic UI update
    setNotifications(prev => prev.filter(n => n.id !== id));

    try {
      await api.delete(`/notifications/${id}`);
    } catch (err) {
      console.error('Failed to remove notification:', err);
      // Revert optimistic update
      setNotifications(previousNotifications);
      toast.error('Failed to delete notification. Please try again.');
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      error,
      addNotification,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      removeNotification,
      fetchNotifications,
      isDrawerOpen,
      setDrawerOpen
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const useNotification = useNotifications;

export const triggerAppNotification = (notification: Omit<Notification, 'id' | 'is_read' | 'createdAt' | 'user_id'>) => {
  window.dispatchEvent(new CustomEvent('app:add-notification', { detail: notification }));
  window.dispatchEvent(new CustomEvent('lympho:add-notification', { detail: notification }));
};
